import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import FloatingActions from '@/pages/home/components/FloatingActions';

const facility = {
  slug: 'garments',
  name: 'BA Knit Composite Ltd.',
  tagline: 'End-to-End Knitwear Manufacturing',
  description: 'A fully integrated knit composite facility with in-house knitting, dyeing, cutting, sewing, and finishing — delivering premium knit apparel from yarn to packed garment.',
  longDescription: 'BA Knit Composite Ltd. is the flagship manufacturing facility of the Beaumonde Tex group, purpose-built for vertically integrated knitwear production. From circular and flatbed knitting through precision dyeing, automated cutting, multi-style sewing, and specialized finishing — every stage happens under one roof. This eliminates vendor handoffs, compresses lead times, and gives buyers a single accountable partner for their knitwear programs. With 5 production lines, 247 skilled operators, and a monthly capacity of 2 lakh pieces, we handle everything from basic tees to complex fashion knitwear with equal precision.',
  heroImage: 'https://readdy.ai/api/search-image?query=Panoramic%20wide%20shot%20of%20a%20modern%20bright%20knitwear%20garment%20factory%20interior%2C%20rows%20of%20organized%20sewing%20production%20lines%20with%20workers%20in%20uniforms%2C%20clean%20white%20walls%20and%20floor%2C%20abundant%20natural%20light%20from%20large%20windows%2C%20professional%20industrial%20architectural%20photography%2C%20warm%20neutral%20atmosphere%2C%20high%20ceiling%20manufacturing%20facility&width=1800&height=800&seq=garments-hero-individual&orientation=landscape',
  capacityStats: [
    { value: '17,800', label: 'Sq. Ft. Facility Space', icon: 'ri-building-4-line' },
    { value: '247', label: 'Total Employees', icon: 'ri-team-line' },
    { value: '5', label: 'Production Lines', icon: 'ri-settings-line' },
    { value: '200', label: 'Total Machines (Pcs)', icon: 'ri-tools-line' },
    { value: '2 Lakh', label: 'Monthly Capacity (Pcs)', icon: 'ri-bar-chart-line' },
    { value: '$5–10M', label: 'Yearly Export Value', icon: 'ri-money-dollar-circle-line' },
  ],
  machines: [
    { name: 'Circular Knitting Machines', qty: '6', spec: 'Mayer & Cie, 28-34 gauge', desc: 'High-speed single and double jersey knitting for body fabrics, collars, and cuffs.' },
    { name: 'Flatbed Knitting Machines', qty: '2', spec: 'Stoll CMS, 14-18 gauge', desc: 'Precision flat knitting for trims, collars, plackets, and shaped panels.' },
    { name: 'Fabric Dyeing Vessels', qty: '6', spec: 'Thies & Fong\'s, 50-500 kg capacity', desc: 'Computer-controlled dyeing with precise color matching and batch consistency.' },
    { name: 'CAD Cutting Tables', qty: '4', spec: 'Gerber Paragon & Zünd', desc: 'Automated fabric spreading and precision cutting with CAD pattern integration.' },
    { name: 'Sewing Lines', qty: '28', spec: 'Juki & Brother industrial machines', desc: 'Multi-style lines configured for t-shirts, polo shirts, sweatshirts, and fashion knitwear.' },
    { name: 'Compacting & Finishing', qty: '3', spec: 'Tubetex & Bianco compactors', desc: 'Dimensional stability control with brushing, peaching, and softening treatments.' },
  ],
  gallery: [
    {
      src: 'https://readdy.ai/api/search-image?query=Industrial%20circular%20knitting%20machines%20in%20a%20clean%20modern%20textile%20factory%2C%20rows%20of%20precision%20machines%2C%20bright%20organized%20workspace%2C%20professional%20manufacturing%20photography%2C%20warm%20neutral%20industrial%20aesthetic&width=700&height=500&seq=garments-gallery-01&orientation=landscape',
      alt: 'Circular Knitting Floor',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Computer-controlled%20fabric%20dyeing%20facility%20with%20stainless%20steel%20vessels%2C%20color%20lab%20station%20with%20spectrophotometer%2C%20clean%20modern%20industrial%20environment%2C%20bright%20lighting%2C%20professional%20textile%20industry%20photography&width=700&height=500&seq=garments-gallery-02&orientation=landscape',
      alt: 'Fabric Dyeing Section',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Automated%20fabric%20cutting%20room%20with%20large%20cutting%20tables%20and%20CAD%20system%2C%20organized%20fabric%20rolls%20on%20racks%2C%20bright%20clean%20industrial%20workspace%2C%20professional%20apparel%20manufacturing%20photography&width=700&height=500&seq=garments-gallery-03&orientation=landscape',
      alt: 'Automated Cutting Room',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Modern%20garment%20sewing%20production%20line%20with%20organized%20workstations%2C%20skilled%20workers%20operating%20industrial%20machines%2C%20bright%20clean%20factory%20environment%2C%20professional%20documentary%20style%20manufacturing%20photography&width=700&height=500&seq=garments-gallery-04&orientation=landscape',
      alt: 'Sewing Production Floor',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Quality%20control%20inspection%20station%20in%20a%20garment%20factory%2C%20inspectors%20examining%20finished%20knitwear%20on%20light%20tables%2C%20bright%20organized%20workspace%2C%20professional%20industrial%20photography&width=700&height=500&seq=garments-gallery-05&orientation=landscape',
      alt: 'Quality Inspection',
    },
    {
      src: 'https://readdy.ai/api/search-image?query=Garment%20finishing%20and%20packing%20area%20with%20folded%20knitwear%20on%20tables%2C%20organized%20workspace%20with%20pressing%20equipment%2C%20clean%20bright%20industrial%20environment%2C%20professional%20product%20photography&width=700&height=500&seq=garments-gallery-06&orientation=landscape',
      alt: 'Finishing & Packing',
    },
  ],
  capabilities: [
    { icon: 'ri-shirt-line', title: 'T-Shirts', desc: 'Basic, fashion, and performance tees in single jersey, pique, and rib fabrics.' },
    { icon: 'ri-t-shirt-line', title: 'Polo Shirts', desc: 'Premium polo shirts with structured collars, plackets, and custom button detailing.' },
    { icon: 'ri-cloud-line', title: 'Sweatshirts', desc: 'Fleece, French terry, and brushed-back sweatshirts in crew, hoodie, and zip styles.' },
    { icon: 'ri-sun-line', title: 'Fashion Knitwear', desc: 'Complex knit structures, jacquards, intarsia, and engineered stripe patterns.' },
    { icon: 'ri-run-line', title: 'Activewear', desc: 'Performance knits with moisture-wicking, anti-microbial, and UV-protection finishes.' },
    { icon: 'ri-heart-line', title: 'Kidswear', desc: 'Safe, soft, and durable children\'s knitwear meeting the strictest safety standards.' },
    { icon: 'ri-moon-line', title: 'Loungewear', desc: 'Comfortable loungewear and sleepwear in soft-hand single jersey and modal blends.' },
    { icon: 'ri-footprint-line', title: 'Basics', desc: 'High-volume basic knitwear programs with consistent quality across millions of pieces.' },
  ],
};

export default function GarmentsPage() {
  const navigate = useNavigate();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
            entry.target.classList.remove('opacity-0', 'translate-y-8');
          }
        });
      },
      { threshold: 0.1 },
    );
    document.querySelectorAll('.animate-on-scroll').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero — Full Bleed Image */}
      <section className="relative h-[500px] md:h-[600px] flex items-end overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${facility.heroImage})` }}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-black/25"></div>
        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 pb-14 md:pb-20">
          <button
            onClick={() => navigate('/sister-concerns')}
            className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors duration-200 mb-5 whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            All Sister Concerns
          </button>
          <span className="inline-block px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold font-heading mb-4">
            Garments
          </span>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-3 leading-tight">
            {facility.name}
          </h1>
          <p className="text-lg md:text-xl text-white/80 font-heading font-medium mb-4">{facility.tagline}</p>
          <p className="max-w-[650px] text-sm md:text-base text-white/65 leading-relaxed">{facility.description}</p>
        </div>
      </section>

      {/* Capacity Dashboard */}
      <section className="relative -mt-10 z-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-white rounded-lg border border-[#E6EAF0] p-6 md:p-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {facility.capacityStats.map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="w-9 h-9 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-2">
                  <i className={`${stat.icon} text-primary-500 text-base`}></i>
                </div>
                <div className="font-heading text-xl md:text-2xl font-bold text-foreground-900">{stat.value}</div>
                <div className="text-[10px] text-foreground-500 mt-1 leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* <section className="pt-16 pb-14 md:pt-24 md:pb-20">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-16">
            <div className="lg:w-3/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
              <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">About the Facility</span>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6 leading-tight">
                Yarn to Garment. One Roof.
              </h2>
              <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-5">{facility.longDescription}</p>
            </div>
            <div className="lg:w-2/5 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-200">
              <div className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0]">
                <h3 className="font-heading text-sm font-semibold text-foreground-800 mb-4 flex items-center gap-2">
                  <i className="ri-settings-3-line text-primary-500"></i>
                  Machine & Equipment
                </h3>
                <div className="space-y-4">
                  {facility.machines.map((m) => (
                    <div key={m.name} className="border-b border-[#E6EAF0] pb-3 last:border-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="text-sm font-semibold text-foreground-800">{m.name}</p>
                        <span className="text-xs font-bold text-primary-500 whitespace-nowrap">{m.qty} units</span>
                      </div>
                      <p className="text-[11px] text-foreground-500 mb-1">{m.spec}</p>
                      <p className="text-xs text-foreground-400 leading-snug">{m.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">Inside the Facility</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Photo Tour</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {facility.gallery.map((img, i) => (
              <div
                key={img.alt}
                className="rounded-lg overflow-hidden border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <img src={img.src} alt={img.alt} className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105" />
                <div className="bg-white px-4 py-3">
                  <p className="text-sm font-medium text-foreground-700">{img.alt}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* <section className="py-14 md:py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12 animate-on-scroll opacity-0 translate-y-8 transition-all duration-700">
            <span className="inline-block text-xs font-semibold tracking-[0.2em] uppercase text-primary-500 mb-4">What We Make</span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">Product Capabilities</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {facility.capabilities.map((cap, i) => (
              <div
                key={cap.title}
                className="bg-background-100 rounded-lg p-6 border border-[#E6EAF0] animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 hover:border-primary-200"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 mb-4">
                  <i className={`${cap.icon} text-primary-500 text-lg`}></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-800 mb-2">{cap.title}</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">{cap.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden bg-primary-500">
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">Ready to Partner with BA Knit Composite?</h2>
          <p className="text-white/80 max-w-[500px] mx-auto mb-8 text-sm md:text-base leading-relaxed">
            From yarn to finished garment, we deliver quality, consistency, and scale — all under one roof.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => navigate('/#contact')}
              className="inline-flex items-center px-8 py-3.5 bg-white text-primary-600 text-sm font-semibold font-heading rounded-md hover:bg-background-100 transition-colors duration-200 whitespace-nowrap"
            >
              Send an Inquiry
              <i className="ri-arrow-right-line ml-2"></i>
            </button>
            <button
              onClick={() => navigate('/sister-concerns')}
              className="inline-flex items-center px-8 py-3.5 bg-transparent border-2 border-white/30 text-white text-sm font-semibold font-heading rounded-md hover:bg-white/10 transition-colors duration-200 whitespace-nowrap"
            >
              All Sister Concerns
            </button>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingActions />
    </div>
  );
}